import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, X, Clock } from 'lucide-react';
import { Bit } from '../../types/bit';

interface SortableBitItemProps {
  bit: Bit;
  onRemove: (id: string) => void;
}

export function SortableBitItem({ bit, onRemove }: SortableBitItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: bit.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 p-4 bg-white rounded-lg shadow-sm border border-gray-200 group hover:border-orange-200 transition-colors duration-150"
    >
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab hover:text-orange-500 text-gray-400 touch-none"
      >
        <GripVertical size={20} />
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2 mb-1">
          <h3 className="font-semibold text-gray-900 truncate">{bit.title}</h3>
          <div className="flex items-center gap-3 flex-shrink-0">
            <div className="flex flex-col items-end text-sm">
              <span className="text-gray-500 flex items-center">
                <Clock size={14} className="mr-1" />
                {bit.estimatedTime.toFixed(1)}m
              </span>
              <span className="text-xs text-gray-400">
                {bit.wordCount} words
              </span>
            </div>
            <button
              onClick={() => onRemove(bit.id)}
              className="md:opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-red-50 rounded-full text-gray-400 hover:text-red-500"
            >
              <X size={16} />
            </button>
          </div>
        </div>
        <p className="text-sm text-gray-600 line-clamp-1">{bit.content}</p>
        {bit.tags && bit.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {bit.tags.map(tag => (
              <span 
                key={tag} 
                className="text-xs bg-gray-50 text-gray-600 px-2 py-0.5 rounded-full"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
